import { cloneDeep, isArray, isEmpty } from 'lodash-es'
import { cleanBlankValue } from 'utils/helper'
import { toDbFormat } from 'utils/formatting'
import { LIMIT_AUTOMATIC_SHOW_GRAND_TOTAL, transType } from 'utils/static'
import dayjs from 'dayjs'

const initialParams = {
  contact_id: '',
  sales_id: null,
  selectedSalesPerson: null,
  shipping_date_from: null,
  shipping_date_to: null,
  payment_date_from: null,
  payment_date_to: null,
  paid_date_from: null,
  paid_date_to: null,
  due_date_from: null,
  due_date_to: null,
  tags: [],
  selectedTags: [],
  selectedGroupContacts: [],
  warehouse_id: [],
  selectedWarehouse_id: [],
  product_id: [],
  selectedProducts: [],
  terms: [],
  selectedTerms: [],
  contact_groups: [],
  selectedCountry_id: {},
  country_id: undefined,
  selectedProvince_id: {},
  province_id: undefined,
  selectedCity_id: {},
  city_id: undefined,
  selectedDistrict_id: {},
  district_id: undefined,
  selectedVillage_id: {},
  village_id: undefined,
  shipping_comp_id: null,
  is_have_attachment: '',
  is_stamped: '',
  production_plan_status_id: '',
  selectedContact: null,
  status_id: '',
  trans_type_ids: transType.INVOICE,
  product_category_ids: [],
  payment_bank_account_ids: [],
  selectedPaymentBankAccounts: [],
}

const initialTableParams = {
  page: 1,
  sort_by: null,
  order_by: null,
}

const initialTransactionDateParams = {
  date_from: toDbFormat(dayjs().subtract(1, 'year')),
  date_to: toDbFormat(dayjs()),
}

/**
 * Parse filters dari URL menjadi params (sesuai logic FinanceInvoices)
 */
export const parseFiltersToParams = (filters) => {
  if (isEmpty(filters)) {
    return {
      params: { ...initialParams },
      tableParams: { ...initialTableParams },
      transationDateParams: { ...initialTransactionDateParams },
    }
  }

  const __filters = cloneDeep(filters)
  const dateFrom = __filters.date_from
  const dateTo = __filters.date_to
  const hasDateRange = !isEmpty(dateFrom) && !isEmpty(dateTo)
  if (hasDateRange) {
    delete __filters.date_from
    delete __filters.date_to
  }
  if (__filters.page) {
    delete __filters.page
  }
  if (__filters.order_by) {
    delete __filters.order_by
  }
  if (__filters.search) {
    delete __filters.search
  }
  if (__filters.sort_by) {
    delete __filters.sort_by
  }

  const newParams = {
    ...initialParams,
    ...__filters,
  }
  if (!isEmpty(newParams.show_columns)) {
    delete newParams.show_columns
  }

  if (!isEmpty(__filters)) {
    if (!isEmpty(newParams.selectedGroupContacts)) {
      newParams.contact_groups = Array.isArray(newParams.selectedGroupContacts)
        ? newParams.selectedGroupContacts.map((row) => row?.id ?? row)
        : []
    }
    if (!isEmpty(newParams.selectedTags)) {
      newParams.tags = Array.isArray(newParams.selectedTags)
        ? newParams.selectedTags.map((row) => row?.id ?? row)
        : []
    }
    if (!isEmpty(newParams.selectedProducts)) {
      newParams.product_id = Array.isArray(newParams.selectedProducts)
        ? newParams.selectedProducts.map((row) => row?.id ?? row)
        : []
    }
    if (!isEmpty(newParams.selectedTerms)) {
      newParams.terms = Array.isArray(newParams.selectedTerms)
        ? newParams.selectedTerms.map((row) => row?.id ?? row)
        : []
    }
    if (!isEmpty(newParams.selectedWarehouse_id)) {
      newParams.warehouse_id = Array.isArray(newParams.selectedWarehouse_id)
        ? newParams.selectedWarehouse_id.map((row) => row?.id ?? row)
        : []
    }
    if (!isEmpty(newParams.selectedCountry_id)) {
      newParams.country_id = newParams.selectedCountry_id?.id ?? newParams.selectedCountry_id
    }
    if (!isEmpty(newParams.selectedProvince_id)) {
      newParams.province_id = newParams.selectedProvince_id?.id ?? newParams.selectedProvince_id
    }
    if (!isEmpty(newParams.selectedCity_id)) {
      newParams.city_id = newParams.selectedCity_id?.id ?? newParams.selectedCity_id
    }
    if (!isEmpty(newParams.selectedDistrict_id)) {
      newParams.district_id = newParams.selectedDistrict_id?.id ?? newParams.selectedDistrict_id
    }
    if (!isEmpty(newParams.selectedVillage_id)) {
      newParams.village_id = newParams.selectedVillage_id?.id ?? newParams.selectedVillage_id
    }
    if (!isEmpty(newParams.selectedPaymentBankAccounts)) {
      newParams.payment_bank_account_ids = Array.isArray(newParams.selectedPaymentBankAccounts)
        ? newParams.selectedPaymentBankAccounts.map((row) => row?.id ?? row)
        : []
    }
  }

  const tableParams = {
    ...initialTableParams,
    page: filters.page ? parseInt(filters.page, 10) : 1,
    order_by: filters.order_by ?? null,
    sort_by: filters.sort_by ?? null,
  }

  const transationDateParams = hasDateRange
    ? { date_from: dateFrom, date_to: dateTo }
    : { ...initialTransactionDateParams }

  return { params: newParams, tableParams, transationDateParams }
}

/**
 * Build formattedParams untuk useInvoices (sesuai logic FinanceInvoices)
 */
export const buildInvoiceListPayload = ({
  params,
  tableParams,
  transationDateParams,
  search = '',
  options = {},
  transTotal = 0,
  showColumns = '',
}) => {
  const perPage = options.sales_per_page_default || 15
  const isGeneral = params.status_id !== 'recurring' && params.status_id !== 'retur'

  const newParams = {
    ...params,
    ...tableParams,
    ...transationDateParams,
    per_page: perPage > 100 ? 100 : perPage,
    tags: Array.isArray(params.tags) ? params.tags.join() : null,
    warehouse_id: Array.isArray(params.warehouse_id) ? params.warehouse_id.join() : null,
    product_id: Array.isArray(params.product_id) ? params.product_id.join() : null,
    terms: Array.isArray(params.terms) ? params.terms.join() : null,
    contact_groups: Array.isArray(params.contact_groups) ? params.contact_groups.join() : null,
    country_id: params.country_id ?? null,
    selectedCountry_id: null,
    province_id: params.province_id ?? null,
    selectedProvince_id: null,
    city_id: params.city_id ?? null,
    selectedCity_id: null,
    district_id: params.district_id ?? null,
    selectedDistrict_id: null,
    village_id: params.village_id ?? null,
    selectedVillage_id: null,
    search: search || null,
    selectedTags: null,
    selectedWarehouse_id: null,
    defaultSalesPerson: null,
    selectedProducts: null,
    selectedContact: null,
    selectedTerms: null,
    selectedGroupContacts: null,
    selectedSalesPerson: null,
    status_id: isGeneral
      ? isArray(params.status_id)
        ? params.status_id.join(',')
        : params.status_id
      : null,
    include_total: transTotal > LIMIT_AUTOMATIC_SHOW_GRAND_TOTAL ? 0 : 1,
    trans_type_ids: isGeneral ? params.trans_type_ids : null,
    show_columns: showColumns || null,
    product_category_ids: Array.isArray(params.product_category_ids)
      ? params.product_category_ids.join()
      : null,
    payment_bank_account_ids: Array.isArray(params.payment_bank_account_ids)
      ? params.payment_bank_account_ids.join()
      : null,
    selectedPaymentBankAccounts: null,
  }

  return cleanBlankValue(newParams)
}
